import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import authService, { TwoFactorChallenge, User, isTwoFactorChallenge } from '../features/auth/auth.service';
import { syncProgressWithAccount } from '../services/readingProgress';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  /** Confirmed email address — required to comment or react. */
  isVerified: boolean;
  loading: boolean;
  /** Resolves to a challenge instead of a user when two-step sign-in is on. */
  login: (username: string, password: string) => Promise<User | TwoFactorChallenge>;
  /** Second step: finishes the sign-in the challenge started. */
  completeTwoFactor: (challenge: string, code: string) => Promise<User>;
  register: (input: { username: string; email: string; password: string }) => Promise<User>;
  logout: () => Promise<void>;
  /** Re-read the session, e.g. straight after confirming an email. */
  refresh: () => Promise<void>;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // The access token is held in memory, so a reload starts with nothing. One
  // silent refresh against the httpOnly cookie restores the session — or
  // confirms there isn't one.
  useEffect(() => {
    let cancelled = false;
    authService
      .bootstrap()
      .then((restored) => {
        if (cancelled) return;
        setUser(restored);
        // Merge whatever this browser recorded while signed out into the
        // account, and take the union back. Fire-and-forget: nothing on screen
        // waits for it, and the local copy is already correct.
        if (restored) void syncProgressWithAccount();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const result = await authService.login({ username, password });

    // A challenge is not a session: nothing is signed in until the code lands.
    if (isTwoFactorChallenge(result)) return result;

    setUser(result);
    void syncProgressWithAccount();
    return result;
  }, []);

  const completeTwoFactor = useCallback(async (challenge: string, code: string) => {
    const signedIn = await authService.completeTwoFactor(challenge, code);
    setUser(signedIn);
    void syncProgressWithAccount();
    return signedIn;
  }, []);

  // Registering signs you in: someone who has just chosen a username and
  // password should not have to type them again on the next screen.
  const register = useCallback(
    async (input: { username: string; email: string; password: string }) => {
      const created = await authService.register(input);
      setUser(created);
      void syncProgressWithAccount();
      return created;
    },
    []
  );

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    const restored = await authService.bootstrap();
    setUser(restored);
    // Social sign-in lands here rather than in login(), so the merge has to
    // happen on this path too.
    if (restored) void syncProgressWithAccount();
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isVerified: Boolean(user?.emailVerified),
    loading,
    login,
    completeTwoFactor,
    register,
    logout,
    refresh,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
